import * as React from 'react';
import { UtilityButtonDiv } from '../../shared/utility_button';
import useDirection from '../../direction/use_direction';
import { IconChevronEnd, IconChevronStart } from '../../utils/chevron_icon';
import { getDisplacementScroll, getTabBounding } from '../utils';
import useEffect from '../../utils/use_effect';

import useStyles from './use_styles';
import useSafeTimeout from '../../utils/use_safe_timeout';
import { useTheme } from '@veneer/theme';
import NotificationBadge from '../../notification_badge';
import { TabListInterface } from './types';

const TabList = React.forwardRef<
  HTMLDivElement,
  TabListInterface &
    Omit<React.HTMLAttributes<HTMLDivElement>, keyof TabListInterface>
>(
  (
    {
      appearance,
      className,
      controlId,
      mode,
      onChangeTab,
      selectedTabId,
      tabs,
      type,
      ...props
    },
    ref
  ) => {
    const [focusedIndex, setFocusedIndex] = React.useState<number>();
    const highlightedRef = React.useRef<HTMLDivElement>(null);

    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    const [setInitialTabTimeout] = useSafeTimeout();

    const tabListRef = React.useRef<HTMLDivElement>(null);
    const { isRTL } = useDirection();
    const isAttached = type === 'attached';
    const isOnline = type === 'online';

    React.useEffect(() => {
      if (focusedIndex === undefined) return;
      highlightedRef?.current?.focus();
    }, [focusedIndex]);

    const shiftScroll = React.useCallback((displacement) => {
      const { current: tabsNode } = tabListRef;

      if (tabsNode && tabsNode.scrollBy) {
        tabsNode.scrollBy({
          left: displacement,
        });
      }
    }, []);

    const handleChange = React.useCallback(
      (event, id, index, isDisabled = false) => {
        if (isDisabled) return;
        event.preventDefault();
        const { current: tabsNode } = tabListRef;

        if (tabsNode) {
          let nextScrollStart = 0;
          const children = Array.from(tabsNode.children);
          const tab = children[index];
          const containerBounding = tabsNode.getBoundingClientRect();
          const { leftBounding, rightBounding } = getTabBounding(tab);

          if (leftBounding < containerBounding.left) {
            // left side of button is out of view
            nextScrollStart = leftBounding - containerBounding.left;
          } else if (rightBounding > containerBounding.right) {
            // right side of button is out of view
            nextScrollStart = rightBounding - containerBounding.right;
          }

          shiftScroll(nextScrollStart);
        }

        if (id !== selectedTabId && onChangeTab) {
          onChangeTab(id);
        }
      },
      [onChangeTab, selectedTabId, shiftScroll]
    );

    const handleKeyDown = React.useCallback(
      (event, index, isDisabled = false) => {
        switch (event.key) {
          case 'Enter':
          case ' ':
            handleChange(event, tabs[index].id, index, isDisabled);
            break;
          case 'End':
            setFocusedIndex(tabs.length - 1);
            break;
          case 'Home':
            setFocusedIndex(0);
            break;
          case 'Left': // IE Compat
          case 'ArrowLeft':
            if (isRTL) {
              if (index + 1 < tabs.length) setFocusedIndex(index + 1);
              else setFocusedIndex(0);
            } else {
              if (index - 1 >= 0) setFocusedIndex(index - 1);
              else setFocusedIndex(tabs.length - 1);
            }
            break;
          case 'Right': // IE Compat
          case 'ArrowRight':
            if (isRTL) {
              if (index - 1 >= 0) setFocusedIndex(index - 1);
              else setFocusedIndex(tabs.length - 1); // Loop to the last tab
            } else {
              if (index + 1 < tabs.length) setFocusedIndex(index + 1);
              else setFocusedIndex(0); // Loop to the first tab
            }
            break;
          default:
            break;
        }
      },
      [handleChange, isRTL, tabs]
    );

    const checkForScrollPosition = React.useCallback(() => {
      const { current: tabsNode } = tabListRef;

      const { scrollLeft, scrollWidth, clientWidth } = tabsNode!;

      const scrollLeftWidth = isRTL ? -scrollLeft : scrollLeft;
      const targetWidth = scrollWidth - clientWidth;

      const canScroll =
        scrollLeftWidth < targetWidth && scrollLeftWidth < targetWidth - 2;

      setCanScrollLeft(isRTL ? canScroll : scrollLeftWidth > 1);
      setCanScrollRight(isRTL ? scrollLeftWidth > 1 : canScroll);
    }, [isRTL]);

    const scrollNavTabsBy = React.useCallback(
      (direction: number) => {
        const { current: tabsNode } = tabListRef;

        const displacementScroll = getDisplacementScroll(
          tabsNode!,
          type,
          canScrollLeft
        );

        shiftScroll(direction * displacementScroll);
      },
      [canScrollLeft, shiftScroll, type]
    );

    const scrollToInitialSelectedTabId = React.useCallback(() => {
      const { current: tabsNode } = tabListRef;

      if (tabsNode) {
        let nextScrollStart = 0;
        const children = Array.from(tabsNode.children);
        const selectedTab = children
          .filter(
            (element) => element.id.toString() === selectedTabId?.toString()
          )
          .pop();

        if (!selectedTab) {
          if (selectedTabId) {
            console.error(
              [
                '@veneer/core: The value provided to the Tab component is invalid.',
                `The Tab with this selectedTabId (\`${selectedTabId}\`) is not part of the document layout.`,
                "Make sure the tab item is present in the document or that it's not display none.",
              ].join('\n')
            );
          }
          return;
        }

        const containerBounding = tabsNode.getBoundingClientRect();
        const { leftBounding, rightBounding } = getTabBounding(selectedTab);

        if (!isRTL && leftBounding > containerBounding.left) {
          nextScrollStart = leftBounding - containerBounding.left;
        } else if (rightBounding < containerBounding.right) {
          nextScrollStart = rightBounding - containerBounding.right;
        }

        shiftScroll(nextScrollStart);
      }
    }, [isRTL, selectedTabId, shiftScroll]);

    useEffect(() => {
      const { current: tabsNode } = tabListRef;

      if (tabsNode) {
        checkForScrollPosition();

        tabsNode.addEventListener('scroll', checkForScrollPosition);
        window.addEventListener('resize', checkForScrollPosition);

        return () => {
          tabsNode.removeEventListener('scroll', checkForScrollPosition);
          window.removeEventListener('resize', checkForScrollPosition);
        };
      }
    }, [checkForScrollPosition]);

    React.useEffect(() => {
      const { current: tabsNode } = tabListRef;

      if (tabsNode) {
        setInitialTabTimeout(() => {
          scrollToInitialSelectedTabId();
        }, 0);
      }
    }, [setInitialTabTimeout, scrollToInitialSelectedTabId]);

    const showPrevious = isRTL ? canScrollRight : canScrollLeft;
    const showNext = isRTL ? canScrollLeft : canScrollRight;

    const isOutlined = isAttached && appearance === 'outlined';
    const isDropShadow = isAttached && appearance === 'dropShadow';
    const isExtended = isOnline && mode === 'extended';
    const isContained = isOnline && mode === 'contained';
    const isNoContentPanel = isAttached && tabs.every((tab) => !tab.content);

    const hasOverflow = showPrevious || showNext;
    const style = useStyles();

    const scrollPrevious = React.useCallback(
      () => scrollNavTabsBy(-1),
      [scrollNavTabsBy]
    );
    const scrollNext = React.useCallback(
      () => scrollNavTabsBy(1),
      [scrollNavTabsBy]
    );

    const theme = useTheme();
    const isHc = theme.mode === 'contrast';
    const isSharp = theme.shape === 'sharp';
    const isPill = theme.shape === 'pill';

    return (
      <div
        className={className}
        css={[
          style.root.base,
          isAttached && [
            (isOutlined || isHc) && [
              !isNoContentPanel && style.root.attached.hcOrOutlined,
              isPill && style.root.attached.pill,
            ],
            isHc && style.root.attached.hc,
            hasOverflow && [
              style.root.attached.overflow.base,
              isHc && [
                style.root.attached.overflow.hc,
                isPill && style.root.attached.overflow.pill,
              ],
            ],

            !hasOverflow && isSharp && style.root.attached.roundNoOverflow,
          ],
          isOnline && [
            !isExtended && !isContained && style.root.online.base,
            isContained && [
              style.root.online.contained.base,
              isHc && style.root.online.contained.hc,
              isPill && style.root.online.contained.pill,
            ],
            !isContained && hasOverflow && style.root.online.extendedHcOverflow,
          ],
        ]}
        {...props}
        ref={ref}
      >
        <UtilityButtonDiv
          aria-hidden
          disabled={!showPrevious}
          icon={<IconChevronStart />}
          onClick={scrollPrevious}
          css={[
            style.utilityButton.base,
            hasOverflow && style.utilityButton.overflow,
          ]}
        />

        <div
          role="tablist"
          ref={tabListRef}
          className="vn-tab-list__tabs"
          css={[
            style.tabs.base,
            isAttached &&
              !hasOverflow && [
                isPill || isSharp
                  ? style.tabs.pillNoOverflow
                  : style.tabs.roundNoOverflow,
              ],
            !isContained && [
              (isOutlined || isExtended) &&
                !isNoContentPanel &&
                style.tabs.outlined,
              isHc && !isNoContentPanel && style.tabs.hc,
            ],
            isOnline && isContained && style.tabs.onlineContained,
            isExtended && style.tabs.extended,
            isDropShadow && [
              style.tabs.dropShadow.base,
              (hasOverflow || isSharp) && style.tabs.dropShadow.overflow,
            ],
            isNoContentPanel && [
              style.tabs.noContentPanel.base,
              isDropShadow && style.tabs.noContentPanel.dropShadow,
            ],
          ]}
        >
          {tabs.map((tab, index) => {
            const hasNotification =
              tab.notificationCount || tab.notificationCount === 0;

            const isSelected = selectedTabId === tab.id;
            const isFocused = index === focusedIndex;
            const isFirst = index === 0;

            const isFocusable =
              focusedIndex !== undefined
                ? isFocused
                : selectedTabId !== undefined
                  ? isSelected
                  : isFirst;

            const isDisabled =
              tab['aria-disabled'] === true ||
              tab['aria-disabled'] === 'true' ||
              tab.disabled;

            return (
              <div
                aria-disabled={isDisabled}
                aria-controls={controlId ? `${controlId}-${tab.id}` : undefined}
                aria-selected={isSelected && !isDisabled}
                className="vn-tab-list__tab"
                id={tab.id.toString()}
                key={tab.id}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={(e) => handleChange(e, tab.id, index, isDisabled)}
                // eslint-disable-next-line react/jsx-no-bind
                onKeyDown={(e) => handleKeyDown(e, index, isDisabled)}
                ref={index === focusedIndex ? highlightedRef : undefined}
                role="tab"
                tabIndex={isFocusable ? 0 : -1}
                css={[
                  isOnline && isContained && style.tab.onlineContained,
                  style.tab.base,
                  isHc && style.tab.hc,
                  isOutlined && !isNoContentPanel && style.tab.outlined,
                  isAttached && [
                    style.tab.attached.base,
                    isHc && !isNoContentPanel && style.tab.attached.hc,
                    isPill && style.tab.attached.pill,
                  ],
                  isOnline && !isContained && style.tab.online,
                  isContained && [
                    style.tab.contained,
                    isPill && style.tab.pill,
                  ],

                  isSelected && [
                    style.tab.selected.base,

                    isAttached && [
                      style.tab.selected.attached.base,
                      isHc && style.tab.selected.attached.hc,
                    ],
                    isOutlined && [
                      style.tab.selected.outlined.base,
                      isHc && style.tab.selected.outlined.hc,
                    ],
                    isDropShadow && style.tab.selected.dropShadow,
                    isHc && style.tab.selected.hc,

                    isOnline && [
                      style.tab.selected.online.base,
                      isHc && style.tab.selected.online.hc,
                      isContained && [
                        style.tab.selected.online.contained.base,
                        isHc && style.tab.selected.online.contained.hc,
                      ],
                    ],
                  ],
                  isDisabled && style.tab.disabled,
                  hasNotification && style.tab.notification,
                ]}
              >
                <div
                  css={[
                    style.label.base,
                    tab.notificationCount && style.label.notification,
                  ]}
                  className="vn-tab-list__label"
                >
                  {tab.label}
                </div>
                {hasNotification && (
                  <NotificationBadge count={tab.notificationCount} />
                )}
              </div>
            );
          })}
        </div>

        <UtilityButtonDiv
          aria-hidden
          disabled={!showNext}
          icon={<IconChevronEnd />}
          onClick={scrollNext}
          css={[
            style.utilityButton.base,
            hasOverflow && style.utilityButton.overflow,
          ]}
        />
      </div>
    );
  }
);

TabList.displayName = 'TabList';

export default TabList;
